export const ROLES = {
  ADMIN: 'admin',
  FISCALIZACION_EDITOR: 'fiscalizacion_editor',
  FISCALIZACION_LECTOR: 'fiscalizacion_lector',
  OBSERVATORIO_EDITOR: 'observatorio_editor',
  OBSERVATORIO_LECTOR: 'observatorio_lector',
  OBSERVATORIO_OBSERVADOR: 'observatorio_observador',
  INFORMES_LECTOR: 'informes_lector',
} as const;

// Type for roles
export type Role = typeof ROLES[keyof typeof ROLES];

// Interface for permissions
export interface RolePermissions {
  // General permissions
  canViewDashboard: boolean;
  
  // Fiscalization permissions
  canManageFiscalizacion: boolean;
  canViewFiscalizacion: boolean;
  canEditFiscalizacion: boolean;
  
  // Observatory permissions
  canManageObservatorio: boolean;
  canViewObservatorio: boolean;
  canEditObservatorio: boolean;
  canObserveObservatorio: boolean;
  canCreateMuestra: boolean;

  // Report permissions
  canSearchAlojamientos: boolean;
}

// Permission map by role
export const ROLE_PERMISSIONS: Record<Role, RolePermissions> = {
  [ROLES.ADMIN]: {
    canViewDashboard: true,
    canManageFiscalizacion: true,
    canViewFiscalizacion: true,
    canEditFiscalizacion: true,
    canManageObservatorio: true,
    canViewObservatorio: true,
    canEditObservatorio: true,
    canObserveObservatorio: true,
    canCreateMuestra: true,
    canSearchAlojamientos: true,
  },
  [ROLES.FISCALIZACION_EDITOR]: {
    canViewDashboard: true,
    canManageFiscalizacion: true,
    canViewFiscalizacion: true,
    canEditFiscalizacion: true,
    canManageObservatorio: false,
    canViewObservatorio: false,
    canEditObservatorio: false,
    canObserveObservatorio: false,
    canCreateMuestra: false,
    canSearchAlojamientos: false,
  },
  [ROLES.FISCALIZACION_LECTOR]: {
    canViewDashboard: true,
    canManageFiscalizacion: false,
    canViewFiscalizacion: true,
    canEditFiscalizacion: false,
    canManageObservatorio: false,
    canViewObservatorio: false,
    canEditObservatorio: false,
    canObserveObservatorio: false,
    canCreateMuestra: false,
    canSearchAlojamientos: false,
  },
  [ROLES.OBSERVATORIO_EDITOR]: {
    canViewDashboard: true,
    canManageFiscalizacion: false,
    canViewFiscalizacion: false,
    canEditFiscalizacion: false,
    canManageObservatorio: true,
    canViewObservatorio: true,
    canEditObservatorio: true,
    canObserveObservatorio: true,
    canCreateMuestra: true,
    canSearchAlojamientos: false,
  },
  [ROLES.OBSERVATORIO_LECTOR]: {
    canViewDashboard: true,
    canManageFiscalizacion: false,
    canViewFiscalizacion: false,
    canEditFiscalizacion: false,
    canManageObservatorio: false,
    canViewObservatorio: true,
    canEditObservatorio: false,
    canObserveObservatorio: false,
    canCreateMuestra: false,
    canSearchAlojamientos: false,
  },
  [ROLES.OBSERVATORIO_OBSERVADOR]: {
    canViewDashboard: true,
    canManageFiscalizacion: false,
    canViewFiscalizacion: false,
    canEditFiscalizacion: false,
    canManageObservatorio: false,
    canViewObservatorio: false,
    canEditObservatorio: false,
    canObserveObservatorio: true,
    canCreateMuestra: false,
    canSearchAlojamientos: false,
  },
  [ROLES.INFORMES_LECTOR]: {
    canViewDashboard: true,
    canManageFiscalizacion: false,
    canViewFiscalizacion: false,
    canEditFiscalizacion: false,
    canManageObservatorio: false,
    canViewObservatorio: false,
    canEditObservatorio: false,
    canObserveObservatorio: false,
    canCreateMuestra: false,
    canSearchAlojamientos: true,
  },
};

// Helper to check if a role has access to a section
export const hasAccessToSection = (role: Role, section: 'fiscalizacion' | 'observatorio'): boolean => {
  switch (section) {
    case 'fiscalizacion':
      return ROLE_PERMISSIONS[role].canViewFiscalizacion;
    case 'observatorio':
      return ROLE_PERMISSIONS[role].canViewObservatorio;
    default:
      return false;
  }
};

// Helper to check if a role can edit in a section
export const canEditInSection = (role: Role, section: 'fiscalizacion' | 'observatorio'): boolean => {
  switch (section) {
    case 'fiscalizacion':
      return ROLE_PERMISSIONS[role].canEditFiscalizacion;
    case 'observatorio':
      return ROLE_PERMISSIONS[role].canEditObservatorio;
    default:
      return false;
  }
}; 